import { apiClient, getDeviceName } from "./client";
import {
  auth,
  organisationStorage,
  preferences,
  registrationStorage,
  StorageKeys,
} from "../services/storage";
import { report } from "@services/Sentry.ts";
import {
  CheckEmailResponse,
  Did,
  LoginWithCodeRequest,
  RegisterRequest,
  SendCodeResponse,
  VerifyCodeResponse,
} from "@/types";

export interface LoginRequest {
  email: string;
  password: string;
}

export interface TwoFactorLoginRequest {
  user_id: Did;
  code?: string;
  recovery_code?: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  password: string;
  password_confirmation: string;
}

export interface LoginResponse {
  token?: string;
  user?: UserLoginResponse;
  two_factor_required?: boolean;
  user_id?: Did;
}

export interface User {
  id: Did;
  name: string;
  email: string;
  role: "admin" | "employee";
  email_verified_at?: string;
  created_at: string;
  updated_at: string;
}

export type UserLoginResponse = Pick<User, "id" | "name" | "email" | "role">;

/**
 * Login with email and password
 * Returns either token/user data or 2FA requirement
 */
export const login = async ({
  email,
  password,
}: LoginRequest): Promise<LoginResponse> => {
  const { data } = await apiClient.post<LoginResponse>("/login", {
    email,
    password,
    device_name: getDeviceName(),
  });

  // If 2FA is required, return the response without storing anything
  if (data.two_factor_required) {
    return data;
  }

  if (data.user) {
    // Store auth token securely
    if (data.token) {
      await auth.saveToken(data.token);
    }

    // Store non-sensitive user info in preferences
    await preferences.set(StorageKeys.USER_ID, data.user.id.toString());
    await preferences.set(StorageKeys.USER_NAME, data.user.name);
    await preferences.set(StorageKeys.USER_ROLE, data.user.role);
  }

  return data;
};

/**
 * Complete 2FA login with code or recovery code
 * Stores token securely and user info in preferences
 */
export const loginWithTwoFactor = async ({
  user_id,
  code,
  recovery_code,
}: TwoFactorLoginRequest): Promise<LoginResponse> => {
  const { data } = await apiClient.post<LoginResponse>("/auth/login/2fa", {
    user_id,
    code,
    recovery_code,
    device_name: getDeviceName(),
  });

  // Store auth token securely
  if (data.token && data.user) {
    await auth.saveToken(data.token);
  }

  // Store non-sensitive user info in preferences
  if (data.user) {
    await preferences.set(StorageKeys.USER_ID, data.user.id.toString());
    await preferences.set(StorageKeys.USER_NAME, data.user.name);
    await preferences.set(StorageKeys.USER_ROLE, data.user.role);
  }

  return data;
};

/**
 * Logout and clear all auth data
 */
export const logout = async (): Promise<void> => {
  try {
    // Try to logout on server (might fail if token expired)
    await apiClient.post("/logout");
  } catch (error: unknown) {
    console.error("Logout API call failed:", error);
    report(error);
  } finally {
    // Always clear local auth data
    await auth.clearAuth();
  }
};

/**
 * Get current authenticated user
 */
export const getCurrentUser = async (): Promise<User> => {
  const { data } = await apiClient.get<{
    user: User;
    two_factor_enabled: boolean;
  }>("/user");

  if (!data || !data.user) {
    throw new Error("No user data received from server");
  }

  return data.user;
};

/**
 * Get current user with 2FA status
 */
export const getCurrentUserWithTwoFactor = async (): Promise<{
  user: User;
  two_factor_enabled: boolean;
}> => {
  const { data } = await apiClient.get<{
    user: User;
    two_factor_enabled: boolean;
  }>("/user");

  if (!data || !data.user) {
    throw new Error("No user data received from server");
  }

  return data;
};

/**
 * Check if user is authenticated
 * - Check if token exists in secure storage
 */
export const checkTokenExists = async (): Promise<boolean> => {
  const token = await auth.getToken();
  return !!token;
};

/**
 * Verify token is still valid by making API call
 */
export const verifyAuth = async (): Promise<User | undefined> => {
  try {
    return await getCurrentUser();
  } catch (reason: unknown) {
    await auth.clearAuth();
    report(reason);

    return undefined;
  }
};

/**
 * Change user password
 */
export const changePassword = async ({
  current_password,
  password,
  password_confirmation,
}: ChangePasswordRequest): Promise<{ message: string }> => {
  const { data } = await apiClient.post<{ message: string }>(
    "/auth/password/change",
    {
      current_password,
      password,
      password_confirmation,
    }
  );

  return data;
};

/**
 * Check if an email address exists in the system
 */
export const checkEmail = async (
  email: string
): Promise<CheckEmailResponse> => {
  const { data } = await apiClient.post<CheckEmailResponse>(
    "/auth/check-email",
    { email }
  );
  return data;
};

/**
 * Send a registration verification code to a new user
 */
export const sendRegistrationCode = async (
  email: string
): Promise<SendCodeResponse> => {
  const { data } = await apiClient.post<SendCodeResponse>(
    "/auth/register/send-code",
    { email }
  );
  // Store email for later use in registration flow
  await registrationStorage.setEmail(email);
  return data;
};

/**
 * Verify a registration code (without consuming it)
 */
export const verifyRegistrationCode = async (
  email: string,
  code: string
): Promise<VerifyCodeResponse> => {
  const { data } = await apiClient.post<VerifyCodeResponse>(
    "/auth/register/verify-code",
    { email, code }
  );
  return data;
};

/**
 * Complete registration with verified code
 */
export const register = async (
  request: Omit<RegisterRequest, "device_name">
): Promise<LoginResponse> => {
  const { data } = await apiClient.post<LoginResponse>("/auth/register", {
    ...request,
    device_name: getDeviceName(),
  });

  // Store auth token securely
  if (data.token && data.user) {
    await auth.saveToken(data.token);
  }

  // Store non-sensitive user info in preferences
  if (data.user) {
    await preferences.set(StorageKeys.USER_ID, data.user.id.toString());
    await preferences.set(StorageKeys.USER_NAME, data.user.name);
  }

  // Clear registration email
  await registrationStorage.clearEmail();

  return data;
};

/**
 * Send a login code to an existing user
 */
export const sendLoginCode = async (
  email: string
): Promise<SendCodeResponse> => {
  const { data } = await apiClient.post<SendCodeResponse>(
    "/auth/login/send-code",
    { email }
  );
  return data;
};

/**
 * Login with a code sent via email
 */
export const loginWithCode = async ({
  email,
  code,
}: Omit<LoginWithCodeRequest, "device_name">): Promise<LoginResponse> => {
  const { data } = await apiClient.post<LoginResponse>("/auth/login/code", {
    email,
    code,
    device_name: getDeviceName(),
  });

  // If 2FA is required, return the response without storing anything
  if (data.two_factor_required) {
    return data;
  }

  // Store auth token securely
  if (data.token && data.user) {
    await auth.saveToken(data.token);
  }

  // Store non-sensitive user info in preferences
  if (data.user) {
    await preferences.set(StorageKeys.USER_ID, data.user.id.toString());
    await preferences.set(StorageKeys.USER_NAME, data.user.name);
  }

  return data;
};

/**
 * Clear all stored organisation data
 */
export const clearOrganisationData = async (): Promise<void> => {
  await organisationStorage.clearCurrentOrganisation();
};
